import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { WorkshopDto } from '@workshop-hst/shared';
import { api, ApiError } from '../lib/api';
import { useToast } from './Toast';

/**
 * Admin fallback for attaching a Zoom recording URL by hand. The webhook should
 * fire automatically when Zoom finishes processing, but webhooks miss sometimes
 * (signature mismatch, downtime, env-var mismatch between Zoom dashboard and
 * the running api). This panel surfaces what's there + lets the admin paste the
 * share URL straight from the Zoom recordings page as a last resort.
 *
 * Only rendered for past workshops in AdminEdit — for upcoming ones the
 * recording obviously doesn't exist yet.
 */
export function RecordingPanel({
  workshop,
  onChange,
}: {
  workshop: WorkshopDto;
  onChange: () => void;
}) {
  const toast = useToast();
  const [url, setUrl] = useState(workshop.recordingPlayUrl ?? '');

  const attach = useMutation({
    mutationFn: (recordingPlayUrl: string) => api.admin.attachRecording(workshop.id, recordingPlayUrl),
    onSuccess: () => {
      toast.success(
        'Recording attached',
        'The past-workshop detail page will now show the player.',
      );
      onChange();
    },
    onError: (err) =>
      toast.error('Attach failed', err instanceof ApiError ? err.message : String(err)),
  });

  const trimmed = url.trim();
  const hasChange = trimmed !== (workshop.recordingPlayUrl ?? '');
  // Quick sanity check — must be an http(s) URL. The backend zod schema also
  // enforces this; this is just so the button stays disabled until the input
  // looks roughly correct.
  const isValidUrl = /^https?:\/\//i.test(trimmed);

  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-card">
      <h3 className="text-sm font-semibold">Recording</h3>
      <p className="mt-1 text-xs text-ink-muted">
        {workshop.recordingPlayUrl
          ? 'A recording URL is set — the past-workshop page is embedding it. Replace it below if needed.'
          : workshop.recordingAvailable
            ? 'A private mirrored recording is set. Paste a URL below only if you need to override it with a manual Zoom share link.'
          : "No recording URL yet. The Zoom webhook usually delivers this automatically — if it hasn't, paste the share URL from Zoom's recording page here."}
      </p>

      {workshop.recordingReadyAt ? (
        <p className="mt-1 text-[11px] text-ink-subtle">
          Last set {new Date(workshop.recordingReadyAt).toLocaleString()}
        </p>
      ) : null}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          type="url"
          className="input flex-1"
          placeholder="https://zoom.us/rec/share/..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button
          type="button"
          className="btn-accent shrink-0"
          disabled={!hasChange || !isValidUrl || attach.isPending}
          onClick={() => attach.mutate(trimmed)}
        >
          {attach.isPending ? 'Saving…' : workshop.recordingPlayUrl ? 'Replace URL' : 'Attach URL'}
        </button>
      </div>
    </section>
  );
}
